import { useAtomValue } from "@effect/atom-react";
import * as Option from "effect/Option";
import { Atom } from "effect/unstable/reactivity";

import { cloudWorkspacesSettledAtom } from "../connection/cloudWorkspaceRegistration";
import { browserBuildEnvironment } from "../env";
import { requiresBrowserAccount } from "../environments/primary/browserEntry.logic";
import { hostEnvironment } from "../connection/hostEnvironment";
import { primaryEnvironmentDiscoveryAtom } from "../connection/primaryEnvironmentDiscovery";
import { environmentPresentations } from "./presentation";
import { environmentShellSummaryAtom } from "./shell";
import {
  resolveWorkspaceShellEntry,
  sameWorkspaceShellEntry,
  type WorkspaceShellEntry,
} from "./workspaceShellEntry.logic";

/**
 * The shell's entry, changing only when its kind or the Environment it names does, so
 * the shell does not re-render on every connection tick behind it.
 */
const workspaceShellEntryAtom = Atom.make((get) => {
  const next = resolveWorkspaceShellEntry({
    runtimeWeb: requiresBrowserAccount(browserBuildEnvironment),
    environmentsSettled:
      get(hostEnvironment.environmentEntriesValueAtom).isReady && get(cloudWorkspacesSettledAtom),
    primaryDiscovery: get(primaryEnvironmentDiscoveryAtom),
    hasSnapshot: get(environmentShellSummaryAtom).hasSnapshot,
    environments: [...get(environmentPresentations.presentationsAtom)].map(
      ([environmentId, presentation]) => ({
        environmentId,
        enabled: presentation.entry.enabled,
        connection: presentation.connection,
      }),
    ),
  });
  const previous = get.self<WorkspaceShellEntry>();
  return Option.isSome(previous) && sameWorkspaceShellEntry(previous.value, next)
    ? previous.value
    : next;
}).pipe(Atom.withLabel("workspace-shell-entry"));

export function useWorkspaceShellEntry() {
  return useAtomValue(workspaceShellEntryAtom);
}
